import { DialogContentText, Stack, Typography } from "@mui/material";
import { ConfirmDialog, ResourceList } from "components/ConfirmDialog";
import pluralize from "pluralize";

import { CredentialFacilityRow } from "./CredentialFacilitiesGrid";

/** A credential can hold thousands of facilities, so name a few and count the rest. */
const MAX_LISTED = 10;

function ChangeGroup({
  title,
  facilities,
}: {
  title: string;
  facilities: CredentialFacilityRow[];
}) {
  if (facilities.length === 0) return null;

  const listed = facilities.slice(0, MAX_LISTED);
  const remaining = facilities.length - listed.length;

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">{title}</Typography>
      <ResourceList
        resources={listed.map((facility) => ({
          resourceType: facility.organizationName ?? "—",
          resourceName: facility.name,
        }))}
      />
      {remaining > 0 && <DialogContentText>{`…and ${remaining} more.`}</DialogContentText>}
    </Stack>
  );
}

/**
 * Summarises the facility links a save is about to write. The staff surface holds
 * facility changes as pending state rather than writing them per click, so this is the
 * only point at which the user sees the whole set together — and an unlink there can
 * reach facilities in an organization they weren't looking at.
 */
export function ConfirmFacilityChanges({
  adds,
  removes,
  loading,
  onConfirm,
  onClose,
}: {
  adds: CredentialFacilityRow[];
  removes: CredentialFacilityRow[];
  loading: boolean;
  onConfirm: () => void | Promise<unknown>;
  onClose: () => void;
}) {
  const total = adds.length + removes.length;

  return (
    <ConfirmDialog
      open
      header="Save these facility changes?"
      confirmText="Yes, Save Changes"
      loading={loading}
      onConfirm={onConfirm}
      onClose={onClose}
    >
      <DialogContentText sx={{ mb: 3 }}>
        {`Saving will update ${total} facility ${pluralize("link", total)} on this credential.`}
      </DialogContentText>
      <Stack spacing={3}>
        <ChangeGroup
          title={`Linking ${adds.length} ${pluralize("facility", adds.length)}`}
          facilities={adds}
        />
        <ChangeGroup
          title={`Unlinking ${removes.length} ${pluralize("facility", removes.length)}`}
          facilities={removes}
        />
      </Stack>
    </ConfirmDialog>
  );
}
